<script lang="ts">
import type { InputProps } from '#layers/ui/components/Input.vue';
import type { NestedSelectMenuItem, NestedSelectMenuTheme } from './types';

export interface NestedSelectMenuLevelProps {
	items: NestedSelectMenuItem[];
	anchorElement: HTMLElement;
	level: number;
	pathSoFar: string[];
	maxNestingDepth?: number;
	searchInput?: boolean | InputProps;
	searchPlaceholder?: string;
	ui: NestedSelectMenuTheme;
	isRtl: boolean;
	/**
	 * Whether to use a portal for the dropdown content.
	 * @defaultValue true
	 */
	portal?: boolean;
}

export interface NestedSelectMenuLevelEmits {
	select: [item: NestedSelectMenuItem, path: string[]];
	collapse: [];
	contentElement: [element: HTMLElement | null];
}
</script>

<script setup lang="ts">
import type { ComponentPublicInstance } from 'vue';

import { useFuse } from '@vueuse/integrations/useFuse';
import {
	ComboboxAnchor,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxGroup,
	ComboboxInput,
	ComboboxItem,
	ComboboxPortal,
	ComboboxRoot,
	ComboboxViewport,
} from 'reka-ui';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useSubmenuSafePath } from '#layers/ui/composables/use-submenu-safe-path';
import { focusFirst } from '#layers/ui/utils/autofocus';
import { injectNestedSelectMenuContext } from './NestedSelectMenuContext';
import NestedSelectMenuItemContent from './NestedSelectMenuItemContent.vue';
import { useNestedSelectMenuKeyboard } from './use-nested-select-menu-keyboard';
import { getChildren, hasChildren, preventOutsideDismissWithinMenu, setExpandedItemRef, stopInputBlurDismissWithinMenu } from './utils';

const props = withDefaults(defineProps<NestedSelectMenuLevelProps>(), {
	maxNestingDepth: 5,
	portal: true,
});

const emit = defineEmits<NestedSelectMenuLevelEmits>();

const slots = defineSlots<{
	'item'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
	'item-leading'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
	'item-label'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
	'item-trailing'?: (props: { item: NestedSelectMenuItem; index: number }) => any;
}>();

const { closeAll, isTargetWithinMenu, menuId } = injectNestedSelectMenuContext();

const searchTerm = ref('');
const expandedItem = ref<NestedSelectMenuItem | null>(null);
const expandedItemRef = ref<HTMLElement | null>(null);
const highlightedItem = ref<NestedSelectMenuItem | null>(null);
const submenuContentElement = ref<HTMLElement | null>(null);

const inputRef = useTemplateRef<HTMLInputElement>('input');
const contentRef = useTemplateRef<ComponentPublicInstance | HTMLElement>('content');
const nestedMenuRef = useTemplateRef<{
	focus: () => void;
	getContentElement: () => HTMLElement | null;
}>('nestedMenu');

const { handleKeydown, expandKey } = useNestedSelectMenuKeyboard({
	isRtl: () => props.isRtl,
	highlightedItem,
	expandedItem,
	nestedMenuRef,
	onEscape: closeAll,
	onCollapse: () => emit('collapse'),
	onSelectHighlighted: handleSelect,
});

const submenuSafePath = useSubmenuSafePath({
	submenuElement: () => submenuContentElement.value ?? nestedMenuRef.value?.getContentElement(),
	side: () => props.isRtl ? 'left' : 'right',
});

const { results: fuzzyResults } = useFuse(searchTerm, () => props.items, {
	fuseOptions: {
		keys: ['label'],
		threshold: 0.3,
	},
	resultLimit: 50,
	matchAllWhenSearchEmpty: true,
});

const filteredItems = computed(() =>
	fuzzyResults.value.map((result) => result.item),
);

const expandedItemChildren = computed((): NestedSelectMenuItem[] =>
	expandedItem.value ? getChildren(expandedItem.value) : [],
);

watch(contentRef, async () => {
	await nextTick();
	emit('contentElement', getContentElement());
}, { flush: 'post' });

watch(expandedItem, () => {
	submenuContentElement.value = null;
});

watch(searchTerm, (term) => {
	if (term) {
		expandedItem.value = null;
		expandedItemRef.value = null;
	}
});

function handleHighlight(item: NestedSelectMenuItem) {
	highlightedItem.value = item;
}

function handleItemMouseenter(item: NestedSelectMenuItem, event: MouseEvent) {
	if (item.disabled) return;
	if (expandedItem.value?.id !== item.id && submenuSafePath.isWithinSafePath(event)) return;

	expandedItem.value = hasChildren(item) ? item : null;
}

function handleSelect(item: NestedSelectMenuItem) {
	const atMaxDepth = props.level >= props.maxNestingDepth;

	if (hasChildren(item) && !atMaxDepth) {
		if (expandedItem.value?.id !== item.id) {
			expandedItem.value = item;
			return;
		}

		if (!item.selectable) {
			// Non-selectable parent items expand, don't select (unless at max depth where they can't expand)
			return;
		}
	}

	emit('select', item, [...props.pathSoFar, item.id]);
}

function handleInputKeydown(event: KeyboardEvent) {
	const input = event.target as HTMLInputElement;
	const atEnd = input.selectionStart === input.value.length;

	if (event.key === expandKey.value && atEnd && highlightedItem.value && hasChildren(highlightedItem.value)) {
		event.preventDefault();
		expandedItem.value = highlightedItem.value;
		void focusNestedMenuAfterExpansion();
	}
}

async function focusNestedMenuAfterExpansion() {
	await nextTick();
	nestedMenuRef.value?.focus();
}

function handleChildCollapse() {
	expandedItem.value = null;
	submenuSafePath.resetSafePath();
	focusFirst(inputRef);
}

function handleChildSelect(item: NestedSelectMenuItem, path: string[]) {
	emit('select', item, path);
}

function handleSubmenuContentElement(element: HTMLElement | null) {
	submenuContentElement.value = element;
}

function handleNestedMenuOutsideEvent(event: Event) {
	preventOutsideDismissWithinMenu(event, isTargetWithinMenu);
}

function handleNestedMenuInputBlur(event: FocusEvent) {
	stopInputBlurDismissWithinMenu(event, isTargetWithinMenu);
}

function handleExpandedItemRef(el: Element | ComponentPublicInstance | null, item: NestedSelectMenuItem) {
	setExpandedItemRef(expandedItem, expandedItemRef, el, item);
}

onMounted(async () => {
	await nextTick();
	emit('contentElement', getContentElement());
});

onBeforeUnmount(() => {
	emit('contentElement', null);
});

defineExpose({
	focus: () => {
		focusFirst(getContentElement());
	},
	getContentElement,
});

function getContentElement(): HTMLElement | null {
	const value = contentRef.value;

	if (!value) return null;
	if (value instanceof HTMLElement) return value;
	if ('$el' in value && value.$el?.nodeType === Node.ELEMENT_NODE) return value.$el as HTMLElement;

	return null;
}
</script>

<template>
	<ComboboxRoot
		open
		ignore-filter
		as-child
		@highlight="(context) => context?.value && handleHighlight(context.value as NestedSelectMenuItem)"
	>
		<ComboboxAnchor :reference="anchorElement" as-child />

		<ComboboxPortal :disabled="!portal">
			<ComboboxContent
				position="popper"
				:side="isRtl ? 'left' : 'right'"
				:side-offset="0"
				align="start"
				:data-nested-select-menu-id="menuId"
				:class="ui.content()"
				@focus-outside="handleNestedMenuOutsideEvent"
				@interact-outside="handleNestedMenuOutsideEvent"
				@keydown="handleKeydown"
				@mouseenter="focusFirst(inputRef)"
				@mousemove="submenuSafePath.handlePointerMove"
				@pointer-down-outside="handleNestedMenuOutsideEvent"
			>
				<DAnimatedSheet>
					<div ref="content" class="flex min-w-full flex-col items-stretch" data-nested-menu-content>
						<ComboboxInput v-if="searchInput" as-child>
							<DInput
								ref="input"
								v-model="searchTerm"
								:placeholder="searchPlaceholder"
								variant="none"
								class="sticky top-0 z-10 bg-default dark:bg-muted"
								:class="[ui.input()]"
								@blur.capture="handleNestedMenuInputBlur"
								@keydown="handleInputKeydown"
							/>
						</ComboboxInput>

						<ComboboxViewport :class="ui.viewport()">
							<ComboboxEmpty :class="ui.empty()">
								{{ $t('common.no_results') }}
							</ComboboxEmpty>

							<ComboboxGroup v-if="filteredItems.length > 0" :class="ui.group()">
								<ComboboxItem
									v-for="(item, index) in filteredItems"
									:key="item.id"
									:ref="(el) => handleExpandedItemRef(el, item)"
									:value="item"
									:data-nested-item="item.id"
									:disabled="item.disabled"
									:class="[
										ui.item({ class: item.ui?.item }),
										expandedItem?.id === item.id && 'text-highlighted before:bg-elevated/50',
									]"
									@mouseenter="handleItemMouseenter(item, $event)"
									@select.prevent="handleSelect(item)"
								>
									<NestedSelectMenuItemContent :item :index :ui :is-rtl>
										<template v-for="(_, name) in slots" #[name]="slotData" :key="name">
											<slot :name v-bind="slotData" />
										</template>
									</NestedSelectMenuItemContent>
								</ComboboxItem>
							</ComboboxGroup>
						</ComboboxViewport>
					</div>
				</DAnimatedSheet>
			</ComboboxContent>
		</ComboboxPortal>
	</ComboboxRoot>

	<NestedSelectMenuLevel
		v-if="expandedItem && expandedItemRef && level < maxNestingDepth"
		ref="nestedMenu"
		:items="expandedItemChildren"
		:anchor-element="expandedItemRef"
		:level="level + 1"
		:path-so-far="[...pathSoFar, expandedItem.id]"
		:max-nesting-depth
		:search-input
		:search-placeholder
		:ui
		:is-rtl
		:portal
		@select="handleChildSelect"
		@collapse="handleChildCollapse"
		@content-element="handleSubmenuContentElement"
	>
		<template v-for="(_, name) in slots" #[name]="slotData" :key="name">
			<slot :name v-bind="slotData" />
		</template>
	</NestedSelectMenuLevel>
</template>
